import { extendObservable, toJS } from 'mobx'
import get from 'lodash/get'

import { projectApi } from '@atheneum/api'

class ProjectSearchStore {
  constructor () {
    extendObservable(this, {
      projects: [],
      query: this.getEmptyQuery(),
      sortOptions: [],
      selected: null,
      total: null,
      bestMatchSort: false,
      openIn: localStorage.getItem('projectSearchStoreOpenIn') || '',
      loading: false,
      error: null
    })
  }

  getEmptyQuery () {
    return {
      keyword: '',
      page: 1,
      account: '',
      employee: '',
      bookmarked: false,
      onlyMy: false,
      country: [],
      industry: [],
      type: '',
      startDate: null,
      endDate: null,
      statusIds: [],
      replyStatus: [],
      eplStatus: [],
      sortBy: 'match'
    }
  }

  select (id) {
    this.selected = id
  }

  setOpenIn (value) {
    localStorage.setItem('projectSearchStoreOpenIn', value)
    this.openIn = value
  }

  changeStatus = statuses => {
    this.query.statusIds = statuses
    this.search()
  }

  clear () {
    this.query = this.getEmptyQuery()
    this.search()
  }

  getQuery () {
    const query = toJS(this.query)
    query.account = get(query, 'account.value', undefined)
    query.employee = get(query, 'employee.value', undefined)
    query.country = this.query.country.map(q => q.value)
    query.industry = this.query.industry.map(q => q.value)
    query.replyStatus = this.query.replyStatus.map(q => q.id)
    query.eplStatus = this.query.eplStatus.map(q => q.id)
    query.type = get(this.query, 'type.name', undefined)
    return query
  }

  search (pageOnly) {
    if (!pageOnly) this.query.page = 1

    this.loading = true
    this.total = null

    const query = this.getQuery()

    this.bestMatchSort = !!query.keyword

    return projectApi
      .searchProjects(query)
      .then(result => {
        const projects = result.hits.hits.map(p => {
          p._source.highlight = p.highlight
          return p._source
        })
        this.projects.replace(projects)

        this.total = result.hits.total
        this.loading = false
      })
      .catch(err => {
        this.error = err
        this.loading = false
      })
  }

  autocompleteAccount = async inputValue => {
    const query = toJS(this.query)
    query.account = get(this, 'query.account.value', undefined)
    query.typeaheadType = 'account'
    query.typeaheadQuery = inputValue

    try {
      const results = await projectApi.searchProjects(query)
      const options = get(results, 'aggregations.account')

      return options.buckets
        .map(i => {
          return {
            id: i.key,
            label: `${i.key} (${i.doc_count})`
          }
        })
        .filter(i => i.id !== 'Not specified')
    } catch (err) {
      console.error('error', err)
      return []
    }
  }

  autocompleteEmployee = async inputValue => {
    const query = toJS(this.query)
    query.employee = get(this, 'query.employee.value', undefined)
    query.typeaheadType = 'employee'
    query.typeaheadQuery = inputValue

    try {
      const results = await projectApi.searchProjects(query)
      const options = get(results, 'aggregations.employee')

      return options.buckets
        .map(i => {
          return {
            id: i.key,
            label: `${i.key} (${i.doc_count})`
          }
        })
        .filter(i => i.id !== 'Not specified')
    } catch (err) {
      console.error('error', err)
      return []
    }
  }

  autocompleteCountry = async inputValue => {
    const query = toJS(this.query)
    query.account = get(this, 'query.countries.value', undefined)
    query.typeaheadType = 'countries'
    query.typeaheadQuery = inputValue

    try {
      const results = await projectApi.searchProjects(query)
      const options = get(results, 'aggregations.countries')

      return options.buckets
        .map(i => {
          return {
            id: i.key,
            label: `${i.key} (${i.doc_count})`
          }
        })
        .filter(i => i.id !== 'Not specified')
    } catch (err) {
      console.error('error', err)
      return []
    }
  }

  autocomplete (value, key) {
    const query = toJS(this.query)

    query.account = get(this, 'query.account.value', undefined)
    query.employee = get(this, 'query.employee.value', undefined)
    query.country = get(this, 'query.country.value', undefined)

    delete query[key]
    query.typeaheadType = key
    query.typeaheadQuery = value

    return projectApi
      .searchProjects(query)
      .then(res => {
        return { options: this.buildRecomendations(res, key) }
      })
      .catch(err => {
        console.error('error', err)
      })
  }

  buildRecomendations (res, key) {
    const options = get(res, 'aggregations.' + key)

    return options.buckets
      .map(i => {
        return {
          value: i.key,
          label: `${i.key} (${i.doc_count})`
        }
      })
      .filter(i => i.value !== 'Not specified')
  }

  changePage (page) {
    this.query.page = page
    this.search(true)
  }
}

export default ProjectSearchStore
